import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

export type VideoEmbedType = 'youtube' | 'vimeo' | 'video' | 'iframe';

export interface VideoEmbed {
  embedType: VideoEmbedType | null;
  safeEmbedUrl: SafeResourceUrl | null;
  videoSrc: string;
}

export function resolveVideoEmbed(url: string, sanitizer: DomSanitizer): VideoEmbed {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, '').toLowerCase();
    const youtubeId = getYouTubeId(parsed, host);
    if (youtubeId) {
      return iframeEmbed('youtube', 'https://www.youtube.com/embed/' + youtubeId, sanitizer);
    }

    const vimeoId = getVimeoId(parsed, host);
    if (vimeoId) {
      return iframeEmbed('vimeo', 'https://player.vimeo.com/video/' + vimeoId, sanitizer);
    }

    if (/\.(mp4|webm|ogg|mov)$/i.test(parsed.pathname) || looksLikeDirectVideo(parsed)) {
      return { embedType: 'video', safeEmbedUrl: null, videoSrc: url };
    }

    return iframeEmbed('iframe', url, sanitizer);
  } catch {
    return iframeEmbed('iframe', url, sanitizer);
  }
}

function iframeEmbed(embedType: VideoEmbedType, url: string, sanitizer: DomSanitizer): VideoEmbed {
  return { embedType, safeEmbedUrl: sanitizer.bypassSecurityTrustResourceUrl(url), videoSrc: '' };
}

function looksLikeDirectVideo(parsed: URL): boolean {
  const host = parsed.hostname.toLowerCase();
  return host.includes('amazonaws.com')
    || host.includes('s3.')
    || /\/videos\//i.test(parsed.pathname);
}

function getYouTubeId(parsed: URL, host: string): string | null {
  if (host === 'youtu.be') {
    return parsed.pathname.split('/').filter(Boolean)[0] || null;
  }
  if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
    if (parsed.searchParams.get('v')) {
      return parsed.searchParams.get('v');
    }
    const parts = parsed.pathname.split('/').filter(Boolean);
    if (parts[0] === 'embed' || parts[0] === 'shorts') {
      return parts[1] || null;
    }
  }
  return null;
}

function getVimeoId(parsed: URL, host: string): string | null {
  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    const parts = parsed.pathname.split('/').filter(Boolean);
    if (parts[0] === 'video') {
      return parts[1] || null;
    }
    return parts[0] || null;
  }
  return null;
}
